import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { sendingDomainsApi } from '@/api/sendingDomains'
import type { SendingDomainWarmupViewDto } from '@/types/sendingDomains'

/**
 * One domain's day, last three days and step ladder. `stateKey` changes when the domain changes state (a restart,
 * a resume, a check that finishes setup), which is when the figures have to be read again.
 */
export function useDomainWarmup(clientCompanyId: string, domainId: string | null, stateKey: string, enabled: boolean) {
  const [warmup, setWarmup] = useState<SendingDomainWarmupViewDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const reload = useCallback(async () => {
    if (!enabled || !domainId) return
    const requestId = ++requestIdRef.current
    const res = await sendingDomainsApi.warmup(clientCompanyId, domainId)
    if (requestIdRef.current !== requestId) return
    setError(res.error ?? null)
    if (res.data) setWarmup(res.data)
  }, [clientCompanyId, domainId, enabled])

  useEffect(() => {
    setWarmup(null)
    setError(null)
    void reload()
    return () => { requestIdRef.current++ }
  }, [reload, stateKey])

  return { warmup, error, reload }
}
